import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TechStackBuilder = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const projects = [
    {
      id: 1,
      name: "E-Commerce Platform",
      description: "Full-stack e-commerce solution with real-time inventory management",
      year: "2024",
      technologies: [
        { name: "React", category: "Frontend", color: "bg-blue-500" },
        { name: "Node.js", category: "Backend", color: "bg-green-600" },
        { name: "PostgreSQL", category: "Database", color: "bg-blue-700" },
        { name: "AWS", category: "Cloud", color: "bg-orange-500" },
        { name: "Docker", category: "DevOps", color: "bg-blue-600" },
        { name: "Redis", category: "Cache", color: "bg-red-500" }
      ],
      metrics: {
        users: "50K+",
        performance: "99.9%",
        loadTime: "< 2s"
      }
    },
    {
      id: 2,
      name: "Analytics Dashboard",
      description: "Real-time data visualization platform for business intelligence",
      year: "2023",
      technologies: [
        { name: "Vue.js", category: "Frontend", color: "bg-green-500" },
        { name: "Python", category: "Backend", color: "bg-yellow-600" },
        { name: "MongoDB", category: "Database", color: "bg-green-700" },
        { name: "D3.js", category: "Visualization", color: "bg-orange-600" },
        { name: "Kubernetes", category: "DevOps", color: "bg-blue-800" }
      ],
      metrics: {
        dataPoints: "1M+",
        queries: "< 100ms",
        uptime: "99.8%"
      }
    },
    {
      id: 3,
      name: "Mobile Banking App",
      description: "Secure mobile banking application with biometric authentication",
      year: "2023",
      technologies: [
        { name: "React Native", category: "Mobile", color: "bg-blue-400" },
        { name: "TypeScript", category: "Language", color: "bg-blue-600" },
        { name: "GraphQL", category: "API", color: "bg-pink-500" },
        { name: "Firebase", category: "Backend", color: "bg-yellow-500" },
        { name: "Jest", category: "Testing", color: "bg-red-600" }
      ],
      metrics: {
        downloads: "100K+",
        rating: "4.8/5",
        security: "Bank Grade"
      }
    }
  ];

  return (
    <div className="bg-white rounded-2xl p-8 shadow-soft border border-border">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-text-primary mb-2">Tech Stack Builder</h2>
        <p className="text-text-secondary">
          Explore how different technologies work together in real projects. Click on a project to see the complete tech stack.
        </p>
      </div>
      <div className="grid gap-6 mb-8">
        {projects?.map((project) => (
          <div
            key={project?.id}
            className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
              selectedProject?.id === project?.id
                ? 'border-accent bg-accent/5 shadow-medium'
                : 'border-border hover:border-accent/50 hover:shadow-soft'
            }`}
            onClick={() => setSelectedProject(project)}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">{project?.name}</h3>
                <p className="text-text-secondary text-sm mb-2">{project?.description}</p>
                <span className="text-xs text-accent font-medium bg-accent/10 px-2 py-1 rounded-full">
                  {project?.year}
                </span>
              </div>
              <Icon 
                name={selectedProject?.id === project?.id ? "ChevronUp" : "ChevronDown"} 
                size={20} 
                className="text-text-secondary" 
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {project?.technologies?.slice(0, 4)?.map((tech, index) => (
                <div
                  key={index}
                  className={`${tech?.color} text-white px-3 py-1 rounded-full text-xs font-medium`}
                >
                  {tech?.name}
                </div>
              ))}
              {project?.technologies?.length > 4 && (
                <div className="bg-gray-400 text-white px-3 py-1 rounded-full text-xs font-medium">
                  +{project?.technologies?.length - 4} more
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {selectedProject && (
        <div className="bg-muted rounded-xl p-6 animate-fade-in-up">
          <h3 className="text-xl font-semibold text-text-primary mb-4">
            {selectedProject?.name} - Complete Tech Stack
          </h3>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-medium text-text-primary mb-3">Technologies Used</h4>
              <div className="space-y-3">
                {selectedProject?.technologies?.map((tech, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${tech?.color}`}></div>
                    <div>
                      <span className="font-medium text-text-primary">{tech?.name}</span>
                      <span className="text-sm text-text-secondary ml-2">({tech?.category})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-text-primary mb-3">Project Metrics</h4>
              <div className="space-y-3">
                {Object.entries(selectedProject?.metrics)?.map(([key, value], index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-text-secondary capitalize">{key?.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="font-semibold text-accent">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
              className="text-accent border-accent hover:bg-accent hover:text-white"
            >
              View Project
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Github"
              iconPosition="left"
              className="text-text-secondary hover:text-text-primary"
            >
              Source Code
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechStackBuilder;